import { FC } from 'react';
import { useTranslation } from 'hooks/api/useTranslation';
import Button from 'shared/ui/button/button';
import { Skeleton } from 'shared/ui/skeleton/skeleton';
import { formatMonthlyPrice } from 'shared/helpers/formatMonthlyPrice';
import { getCurrencySymbol } from 'shared/helpers/getCurrencySymbol';
import { formatBillingDate } from './order-summary.utils';
import { OrderSummaryProps } from './order-summary.types';
import cls from './order-summary.module.scss';

export const OrderSummary: FC<OrderSummaryProps> = ({
  quantity,
  preview,
  isLoading,
  haveBase = false,
  isError = false,
  onRetry,
  onProceed,
}) => {
  const { t, currentLocale } = useTranslation();
  const isWaiting = !preview && !isError;
  const billingDateLabel = preview
    ? formatBillingDate(preview.next_billing_date, currentLocale)
    : '';
  const currency = preview?.currency ?? 'USD';
  const vatRate = preview?.vat_rate ?? 0;
  const addonPrice =
    preview?.line_items.find(li => li.product_id === 2)?.amount ?? 19;
  const addonItemTotal = quantity === 0 ? 0 : quantity * addonPrice;
  const monthlyInclVat =
    quantity === 0 && haveBase ? 0 : (preview?.monthly_total_incl_vat ?? 0);
  const monthlyExclVat =
    quantity === 0 && haveBase ? 0 : (preview?.monthly_total_excl_vat ?? 0);
  const firstPayment =
    quantity === 0 && haveBase
      ? 0
      : (preview?.prorated_first_payment_incl_vat ?? 0);

  if (isError) {
    return (
      <div className={cls.orderSummary}>
        <div className={cls.head}>
          <p className={cls.title}>{t('buySubscriptionModalSummaryTitle')}</p>
        </div>
        <div className={cls.errorState} role='alert'>
          <p className={cls.errorTitle}>
            {t('buySubscriptionModalErrorTitle')}
          </p>
          <p className={cls.errorDescription}>
            {t('buySubscriptionModalErrorDescription')}
          </p>
          {onRetry && (
            <Button theme='outlined' tone='default' onClick={onRetry}>
              {t('tryAgain')}
            </Button>
          )}
        </div>
        <div className={cls.proceed}>
          <div className={cls.proceedBtn}>
            <Button
              theme='filled'
              tone='default'
              className={cls.proceedButton}
              onClick={onProceed}
              disabled
            >
              {t('proceedToPayment')}
            </Button>
          </div>
          <p className={cls.fine}>{t('buySubscriptionModalReassurance')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={cls.orderSummary}>
      <div className={cls.head}>
        <p className={cls.title}>{t('buySubscriptionModalSummaryTitle')}</p>
        <div className={cls.subtitle}>
          {isWaiting ? (
            <Skeleton width={160} height={16} />
          ) : (
            t('buySubscriptionModalNextBillingDate', '_global', '', {
              date: billingDateLabel,
            })
          )}
        </div>
      </div>
      <div className={cls.top}>
        <div className={cls.rows}>
          {quantity === 0 ? (
            haveBase ? (
              <p className={cls.emptyNote}>
                {t('buySubscriptionModalEmptyState')}
              </p>
            ) : (
              <div className={cls.row}>
                <span className={cls.label}>
                  {t('buySubscriptionModalLineItem', '_global', '', {
                    product: t('rivaLicense'),
                    qty: 1,
                  })}
                </span>
                <span>
                  {isWaiting ? (
                    <Skeleton width={80} height={16} />
                  ) : (
                    formatMonthlyPrice(
                      preview?.line_items.find(li => li.product_id === 1)
                        ?.amount ?? 49,
                      currency,
                      t('month')
                    )
                  )}
                </span>
              </div>
            )
          ) : (
            <>
              {!haveBase && (
                <div className={cls.row}>
                  <span className={cls.label}>
                    {t('buySubscriptionModalLineItem', '_global', '', {
                      product: t('rivaLicense'),
                      qty: 1,
                    })}
                  </span>
                  <span>
                    {isWaiting ? (
                      <Skeleton width={80} height={16} />
                    ) : (
                      formatMonthlyPrice(
                        preview?.line_items.find(li => li.product_id === 1)
                          ?.amount ?? 49,
                        currency,
                        t('month')
                      )
                    )}
                  </span>
                </div>
              )}
              <div className={cls.row}>
                <span className={cls.label}>
                  {t('buySubscriptionModalLineItem', '_global', '', {
                    product: `${t('additional')}  ${t('license')}`,
                    qty: quantity,
                  })}
                </span>
                <span>
                  {isWaiting ? (
                    <Skeleton width={80} height={16} />
                  ) : (
                    formatMonthlyPrice(addonItemTotal, currency, t('month'))
                  )}
                </span>
              </div>
            </>
          )}
        </div>
        <div className={cls.divider} />
        <div className={cls.rows}>
          <p className={cls.sectionTitle}>
            {t('buySubscriptionModalMonthlyTotal')}
          </p>
          <div className={cls.row}>
            <span className={cls.label}>
              {isWaiting ? (
                <Skeleton width={100} height={16} />
              ) : (
                t('buySubscriptionModalInclVat', '_global', '', {
                  rate: vatRate,
                })
              )}
            </span>
            <span className={cls.strong}>
              {isWaiting ? (
                <Skeleton width={80} height={16} />
              ) : (
                formatMonthlyPrice(monthlyInclVat, currency, t('month'))
              )}
            </span>
          </div>
          <div className={cls.row}>
            <span className={cls.label}>
              {t('buySubscriptionModalExclVat')}
            </span>
            <span>
              {isWaiting ? (
                <Skeleton width={80} height={16} />
              ) : (
                formatMonthlyPrice(monthlyExclVat, currency, t('month'))
              )}
            </span>
          </div>
        </div>
        <div className={cls.firstPaymentCard}>
          <div className={cls.row}>
            <span className={cls.fpStrong}>
              {t('buySubscriptionModalFirstPayment')}
            </span>
            <span className={cls.fpStrong}>
              {isWaiting ? (
                <Skeleton width={60} height={16} />
              ) : (
                `${getCurrencySymbol(currency)}${firstPayment.toFixed(2)}`
              )}
            </span>
          </div>
          <div className={cls.note}>
            {isWaiting ? (
              <Skeleton width={180} height={14} />
            ) : (
              t('buySubscriptionModalFirstPaymentNote', '_global', '', {
                date: billingDateLabel,
              })
            )}
          </div>
        </div>
      </div>
      <div className={cls.proceed}>
        <div className={cls.proceedBtn}>
          <Button
            theme='filled'
            tone='default'
            className={cls.proceedButton}
            onClick={onProceed}
            disabled={(haveBase && quantity === 0) || isLoading || !preview}
          >
            {t('proceedToPayment')}
          </Button>
        </div>
        <p className={cls.fine}>{t('buySubscriptionModalReassurance')}</p>
      </div>
    </div>
  );
};
